import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './SetupGuide.css';

function SetupGuide() {
  const [activeStep, setActiveStep] = useState(0);
  
  const steps = [
    {
      title: "Connect your HubSpot account",
      content: "Log in to your ZipCode AutoFill dashboard and click on 'Connect with HubSpot'. You'll be prompted to authorize the integration with your HubSpot account.",
      icon: (
        <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#FF7A59"><path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z"/></svg>
      )
    },
    {
      title: "Review your field mappings",
      content: "When you connect a new portal, we automatically set up default mappings to HubSpot's standard City, State/Region, and Country fields for both Contacts and Companies, plus Timezone updates for Contacts. Click 'Configure' on your connected portal to customize mappings for states, provinces, regions, counties, or districts. For global support, enable the experimental 'International lookup' option and save your configuration.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      )
    },
    {
      title: "Ensure You Have Enough Credits",
      content: (
        <>
          Successful standard lookups consume 1 credit, and successful international lookups consume 2 credits. Make sure you have sufficient credits in your account before performing lookups. Every user gets 50 free credits upon registering. You can purchase more credits from the <Link to="/credits" style={{ color: 'var(--primary-color)', textDecoration: 'underline' }}>Credits</Link> page.
        </>
      ),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
          <path d="M5.5 9.511c.076.954.83 1.697 2.182 1.785V12h.6v-.709c1.4-.098 2.218-.846 2.218-1.932 0-.987-.626-1.496-1.745-1.76l-.473-.112V5.57c.6.068.982.396 1.074.85h1.052c-.076-.919-.864-1.638-2.126-1.716V4h-.6v.719c-1.195.117-2.01.836-2.01 1.853 0 .9.606 1.472 1.613 1.707l.397.098v2.034c-.615-.093-1.022-.43-1.114-.9zm2.177-2.166c-.59-.137-.91-.416-.91-.836 0-.47.345-.822.915-.925v1.76h-.005zm.692 1.193c.717.166 1.048.435 1.048.91 0 .542-.412.914-1.135.982V8.518z"/>
          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
          <path d="M8 13.5a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11m0 .5A6 6 0 1 0 8 2a6 6 0 0 0 0 12"/>
        </svg>
      )
    },
    {
      title: "Start using ZipCode AutoFill",
      content: "Add or change a ZIP or postal code in HubSpot manually, through a workflow, or on a form to populate your configured location fields. With International lookup enabled, every record needs a country, including US and Canadian records. A missing or unrecognized country means no lookup, no update, and no credit charge. Fill in the country first, then update the postal code; changing only the country does not trigger a lookup. Available fields vary by location, and missing or ambiguous fields remain unchanged.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 11 12 14 22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      )
    },
  ];

  const handleStepClick = (index) => {
    setActiveStep(index);
  };

  return (
    <div className="setup-guide-container">
      <div className="setup-guide-header">
        <div className="guide-shapes">
          <div className="guide-shape shape1"></div>
          <div className="guide-shape shape2"></div>
          <div className="guide-shape shape3"></div>
        </div>
        <h1>Setup Guide</h1>
        <p className="guide-intro">
          Setting up ZipCode AutoFill with your HubSpot account is quick and easy.
          Follow these steps to start automatically populating address fields from ZIP and postal codes worldwide.
        </p>
      </div>
      
      <div className="setup-steps-container">
        <div className="steps-navigation">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={`step-nav-item ${activeStep === index ? 'active' : ''}`}
              onClick={() => handleStepClick(index)}
            >
              <div className="step-number">{index + 1}</div>
              <div className="step-title">{step.title}</div>
            </div>
          ))}
        </div>
        
        <div className="step-content-container">
          <div className="step-icon">{steps[activeStep].icon}</div>
          <h2>{steps[activeStep].title}</h2>
          <p>{steps[activeStep].content}</p>
        </div>
      </div>
      
      <div className="additional-info">
        <h2>Additional Information</h2>
        
        <div className="info-cards">
          <div className="info-card">
            <div className="info-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </div>
            <h3>Our Service</h3>
            <p>
              ZipCode AutoFill streamlines address entry in HubSpot by automatically populating city, state or province,
              county or district, country, and timezone information. International lookup uses a postal code and country together.
            </p>
          </div>
          
          <div className="info-card">
            <div className="info-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="12" y1="8" x2="12" y2="16" />
              </svg>
            </div>
            <h3>Backfilling Existing Records</h3>
            <p>
              For customers with access to HubSpot workflows, you can backfill location data for existing contacts with ZIP or postal codes.
              Make sure each record has its country populated before running international lookups.
              Watch our tutorial video to learn how to set this up.
            </p>
            <div className="video-container">
              <iframe 
                src="https://www.youtube.com/embed/NbnRMGvPW14" 
                title="ZipCode AutoFill Backfilling Tutorial" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen>
              </iframe>
            </div>
          </div>
        </div>
      </div>
      <br />
      <div className="setup-cta">
        <h2>Ready to Get Started?</h2>
        <p>Follow the steps above to connect your HubSpot account and start using ZipCode AutoFill.</p>
        <Link to="/dashboard" className="cta-button">Go to Dashboard</Link>
      </div>
    </div>
  );
}

export default SetupGuide;
